import { Box, Button, CircularProgress, MobileStepper } from '@mui/material';
import { GridColDef, GridColumnHeaderParams, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import FlexBox from 'components/flex-box';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Tooltip from 'components/tooltip';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Info } from 'lucide-react';

import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { getWaterQualityType, postWaterQuality } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { CreateWaterQualityInput } from 'views/production-systems/features/farm-overview/types/create-water-quality-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { Pond } from 'views/production-systems/types/pond.type';
import { AmPm, WaterQuality } from 'views/production-systems/types/water-quality.type';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import { getCycleName } from 'views/production-systems/utils/get-cycle-name';
import RegistrationHeader from '../../../../components/registration-header';
import { hasValueError } from '../../../../utils/water-quality-utils';
import ListSelectedPonds from '../list-selected-ponds';
import DuplicateRegistrationWarning, { REGISTRATION_EXISTS_QUERY_KEY } from '../duplicate-registration-warning';
import { RegistrationActionType } from '../../types/registration-action-type.enum';

type Props = {
  ponds: Pond[];
  handleClose?: () => void;
};

type ParamTypes = {
  globalSiteId: string;
};

type Row = {
  pondId: string;
  pondName: string;
  cycleId: number;
  zoneName: string;
  cycleName: string;
  currentMw: number;
  currentQty: number;
  currentBm: number;
  waterQualityTypeId: number;
  value: number;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.waterQuality';

const AddWaterQuality: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, registrationDate } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [selectedPonds, setSelectedPonds] = useState<Pond[]>(ponds);
  const [dayTime, setDayTime] = useState<AmPm>(AmPm.AM);

  //#region FETCH DATA

  const { data: waterQualityTypes } = useGet(['getWaterQualityType', params.globalSiteId], ({ queryKey }) =>
    getWaterQualityType(String(queryKey[1]))
  );

  //#endregion FETCH data

  //#region Data GRID

  const initialRows = useMemo(() => {
    return selectedPonds.map((pond) => ({
      pondName: pond.pondName,
      pondId: pond.pondId,
      cycleName: pond.cycleName,
      cycleId: pond.cycleId,
      zoneName: pond.zoneName,
      currentMw: pond.currentMw,
      currentQty: pond.currentQty,
      currentBm: calculateBiomass(pond.currentMw, pond.currentQty),
      waterQualityTypeId: 0,
      value: 0,
    }));
  }, [selectedPonds]);

  const [rows, setRows] = useState(initialRows || []);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow };
    setRows(
      rows.map((row) => {
        if (row.pondId === newRow.pondId) {
          return updatedRow;
        }

        return { ...row, waterQualityTypeId: !row.waterQualityTypeId ? updatedRow.waterQualityTypeId : row.waterQualityTypeId }; // better way to do this?
      })
    );
    return updatedRow;
  };

  const columns: GridColDef[] = [
    {
      field: 'pondName',
      flex: 2,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destinationPond` }),
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Box display={'flex'} gap={2} alignItems={'center'} width={'100%'}>
            {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
            <Box overflow={'hidden'} textOverflow={'ellipsis'} title={row.pondName}>
              {row.pondName}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'cycleName',
      flex: 1,
      editable: false,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.cycleName` }),
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'zoneName',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.zone` }),
      type: 'string',
      editable: false,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
    },
    {
      field: 'currentQty',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.number` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: false,
      flex: 1,
    },
    {
      field: 'currentMw',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.meanWeight` }),
      sortable: false,
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
    },
    {
      field: 'currentBm',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.biomass` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: false,
      flex: 1,
    },
    {
      field: 'waterQualityTypeId',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.waterQualityType` }),
      type: 'singleSelect',
      align: 'left',
      headerAlign: 'left',
      valueOptions: waterQualityTypes?.map((type) => ({
        value: type.id,
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.types.${type.id}` }),
      })),
      editable: true,
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = !params.props.value;
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-waterQualityTypeId`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'value',
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <Box display={'inline-flex'} alignItems={'center'}>
            <FormattedMessage id={`${PAGE_NAME_ID}.add.value`} />
            <span>
              <Tooltip
                title={
                  <Box display={'flex'} flexDirection={'column'} justifyContent={'space-around'} gap={4}>
                    <Box display={'flex'} flexDirection={'row'}>
                      {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.1' })}:
                      {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '20' })}
                    </Box>
                    <Box display={'flex'} flexDirection={'row'}>
                      {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.2' })}:
                      {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '120' })}
                    </Box>
                    <Box display={'flex'} flexDirection={'row'}>
                      {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.3' })}:
                      {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '15', upper: '45' })}
                    </Box>
                    <Box display={'flex'} flexDirection={'row'}>
                      {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.4' })}:
                      {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '60' })}
                    </Box>
                    <Box display={'flex'} flexDirection={'row'}>
                      {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.5' })}:
                      {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '14' })}
                    </Box>
                  </Box>
                }>
                <Info size={18} style={{ marginRight: 4, marginLeft: 4 }} />
              </Tooltip>
            </span>
          </Box>
        );
      },
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      editable: true,
      type: 'number',
      cellClassName: (params) => {
        const { value, waterQualityTypeId } = params.row;
        const hasError = hasValueError(value, waterQualityTypeId);

        return hasError ? 'cell--warning' : '';
      },
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const { value } = params.props;
        const waterQualityTypeId = params.row.waterQualityTypeId;
        const error = value === null || hasValueError(value, waterQualityTypeId);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-value`]: error });
        return { ...params.props, error };
      },
    },
  ] as GridColDef[];

  const summaryValues: SummaryItem[] = useMemo(() => {
    let newSummary = selectedPonds.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.currentMw, curr.currentQty);

        return {
          currentQty: prev.currentQty + curr.currentQty,
          currentBm: biomass + prev.currentBm,
          currentMw: 0,
        };
      },
      {
        currentMw: 0,
        currentQty: 0,
        currentBm: 0,
      }
    );

    newSummary.currentMw = calculateMeanWeight(newSummary.currentBm, newSummary.currentQty);

    return [
      {
        value: undefined, // Empty ListItem with 'hideExtraBorders' class
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.currentQty,
        type: 'number',
        hidden: false,
      },
      {
        value: (newSummary.currentBm / newSummary.currentQty) * 1000,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentBm,
        type: 'number',
        hidden: false,
      },
      {
        value: undefined, // Empty ListItem with 'hideExtraBorders' class
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem with 'hideExtraBorders' class
        type: undefined,
        hidden: true,
      },
    ];
  }, [selectedPonds]);

  //#endregion Data Grid

  //#region HANDLERS

  const handleSubmit = () => {
    let d: CreateWaterQualityInput = {
      date: registrationDate.format('YYYY-MM-DD'),
      cycles: rows.map((row) => ({
        cycleId: row.cycleId,
        waterQualityTypeId: row.waterQualityTypeId,
        value: row.value,
        pondId: row.pondId,
        amPm: dayTime,
      })),
    };

    createWaterQuality.mutate({ globalSiteId, data: d });
  };

  //#endregion

  //#region POST DATA

  const createWaterQuality = usePost<WaterQuality, { globalSiteId: string; data: CreateWaterQualityInput }>(
    (request) => postWaterQuality(request.globalSiteId, request.data),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      !!handleClose && handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion POST DATA

  //#region HOOKS

  const [activeStep, setActiveStep] = React.useState(0);
  const isCheckingDuplicates = useIsFetching({ queryKey: [REGISTRATION_EXISTS_QUERY_KEY] }) > 0;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => !row.waterQualityTypeId || hasValueError(row.value, row.waterQualityTypeId));
  }, [rows]);

  //#endregion HOOKS

  return lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      {activeStep === 0 && (
        <FlexBox>
          <ListSelectedPonds selectedPonds={selectedPonds} setSelectedPonds={setSelectedPonds} />
        </FlexBox>
      )}
      {activeStep === 1 && (
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <DuplicateRegistrationWarning actionTypeId={RegistrationActionType.WaterQuality} ponds={selectedPonds} />
          <RegistrationHeader />
          <Box display="flex" justifyContent="flex-end" alignItems={'center'}>
            <Select
              dark
              label={''}
              hiddenLabel
              value={dayTime}
              size="small"
              onChange={(event) => {
                setDayTime(event.target.value as AmPm);
              }}>
              {Object.entries(AmPm)?.map((time) => (
                <RadioSelectItem key={time[0]} value={time[1]}>
                  {time[0]}
                </RadioSelectItem>
              ))}
            </Select>
          </Box>
          <DataGrid
            rows={rows}
            apiRef={apiRef}
            columns={columns}
            showToolbar
            slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: `${intl.formatMessage({
                    id: 'productionSystems.registrationLog.registrationTypes.waterQualitySample',
                  })}-${dayjs().format('DD/MM/YYYY')}`,
                },
              },
              footer: {
                summaryValues: summaryValues,
              } as DataGridSummaryProps,
            }}
            getRowId={(row) => row.pondId}
            processRowUpdate={processRowUpdate}
            initialState={{
              sorting: {
                sortModel: [{ field: 'pondId', sort: 'asc' }],
              },
            }}
            onCellEditStop={handleEditing}
            onRowClick={({ id }) => {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
          />
        </Box>
      )}
      <MobileStepper
        steps={2}
        position="static"
        activeStep={activeStep}
        nextButton={
          activeStep === 1 ? (
            <Button
              disabled={createWaterQuality.isPending || disableSubmit || isEditing || hasError || isCheckingDuplicates}
              onClick={handleSubmit}
              startIcon={createWaterQuality.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.submit" values={{ type: '' }} />
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={activeStep === 1}>
              <FormattedMessage id="general.next" />
            </Button>
          )
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <FormattedMessage id="general.back" />
          </Button>
        }
      />
    </Box>
  );
};

export default React.memo(AddWaterQuality);
